import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchFileCategoryById } from "services/files-category.service";
import { RootState } from "configureStore";

// Define the types for your initial state
interface DocumentContainerState {
  isLoading: boolean;
  isError: boolean;
  errMsg: string;
  categoryType: "external" | "internal" | null;
  catFilesData: any | null;
}

// Define the initial state
const initialState: DocumentContainerState = {
  isLoading: false,
  isError: false,
  errMsg: "",
  categoryType: null,
  catFilesData: null,
};

// Create the slice
const documentFilesContainerSlice = createSlice({
  name: "categoryFiles",
  initialState,
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(state: DocumentContainerState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
      // Starting a fresh load clears any previous error so the UI can recover.
      if (action.payload) {
        state.isError = false;
        state.errMsg = "";
      }
    },
    setError(
      state: DocumentContainerState,
      action: PayloadAction<{ error: string }>
    ) {
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setFilesContainer(
      state: DocumentContainerState,
      action: PayloadAction<any>
    ) {
      state.catFilesData = action.payload;
      state.isLoading = false;
    },
    setCategoryType(state: DocumentContainerState, action: PayloadAction<any>) {
      state.categoryType = action.payload;
    },
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = documentFilesContainerSlice;

// Extract and export each action creator by name
export const {
  setDestroy,
  setLoading,
  setError,
  setFilesContainer,
  setCategoryType,
} = actions;

// Export the reducer, either as a default or named export
export default reducer;

// Define the type for the dispatch function
export const fetchCategoryFilesDetails =
  ({
    companyId,
    catId,
    source,
    page,
    pageSize,
    dateFrom,
    dateTo,
    userId,
    customerId,
    isSigned,
  }: {
    companyId: string;
    catId: string;
    source?: string;
    page?: number;
    pageSize?: number;
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    customerId?: string;
    isSigned?: string;
  }) =>
  async (dispatch: any, getState: () => RootState) => {
    dispatch(setLoading(true));
    try {
      const state = typeof getState === "function" ? getState() : undefined;
      const existingData = state?.documentFilesReducer?.catFilesData;
      const existingPagination = existingData?.pagination ?? {};

      const requestedPage =
        typeof page === "number" && Number.isFinite(page) ? page : undefined;
      const requestedPageSize =
        typeof pageSize === "number" && Number.isFinite(pageSize)
          ? pageSize
          : undefined;

      const effectivePage =
        typeof requestedPage === "number" && requestedPage > 0
          ? requestedPage
          : Number.isFinite(existingPagination?.page) &&
            existingPagination.page > 0
          ? existingPagination.page
          : undefined;
      const effectivePageSize =
        typeof requestedPageSize === "number" && requestedPageSize > 0
          ? requestedPageSize
          : Number.isFinite(existingPagination?.pageSize) &&
            existingPagination.pageSize > 0
          ? existingPagination.pageSize
          : undefined;

      // const data = await fetchFileCategoryById(companyId, catId);
      const categoryData = await fetchFileCategoryById(
        companyId,
        catId,
        source,
        {
          page: effectivePage,
          pageSize: effectivePageSize,
          dateFrom,
          dateTo,
          userId,
          customerId,
          isSigned,
          // A parent category shows the files of all of its subcategories, and
          // filters search across that whole subtree.
          includeSubcategories: true,
        }
      );
      dispatch(setFilesContainer(categoryData));
      dispatch(setCategoryType(categoryData.type));
    } catch (e) {
      const error = "not_fetch";
      dispatch(setError({ error }));
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };
